import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { RecentlyViewedContext } from '@components/RecentlyViewed/RecentlyViewedProvider'

import { HomeProductReflectionsQueryResult } from '@graphql/queries/__generated__/home.generated'
import Skeleton from '@mui/material/Skeleton'
import makeStyles from '@mui/styles/makeStyles'
import PlaceHolder from 'images/placeholder-img.svg'
import _ from 'lodash'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link } from 'react-router-dom'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { maybe } from '../../utils/misc'
import FormattedPrice from '@components/FormattedPrice'
import ProductDetailsDialog from '@components/Product/ProductDetailsDialog'
import { getProductUrl } from '../../urls'
import {
  useProductReflectionsLazyQuery,
  useProductReflectionsQuery,
} from '@graphql/queries/__generated__/reflection.generated'
import { useTranslation } from 'react-i18next'
import { useIsVisible } from '../../hooks/useIsVisible'
import { CartTypeEnum, Ordering } from '../../../generated/gqlTypes'

const MAX_SHOW_PRODUCTS = 18
const useStyles = makeStyles(theme => ({
  root: {
    '&:hover $detailsWrapper': {
      opacity: 1,
    },
  },
  detailsWrapper: {
    opacity: 0,
    transition: 'opacity .25s ease',
    backgroundColor: 'hsl(0deg 0% 100% / 80%)',
  },
  placeHolder: {
    paddingBottom: '137%',
  },
}))

const RecentlyViewedSkeleton: React.FC<{ swiperOptions: SwiperOptions }> = ({ swiperOptions }) => {
  const classes = useStyles()
  return (
    <Swiper {...swiperOptions}>
      {_.range(6).map((number, idx) => {
        return (
          <SwiperSlide key={idx}>
            <div className={`relative block no-underline ${classes.root}`}>
              <div className='absolute inset-0 h-full'>
                <LazyLoadImage
                  alt={`New Product Skeleton ${number}`}
                  effect='opacity'
                  src={PlaceHolder}
                  wrapperClassName='w-full'
                  className='w-full h-full object-contain object-top'
                />
              </div>
              <div className={classes.placeHolder} />
              <div className='md:hidden pt-12'>
                <div className='text-13 line-clamp-3'>
                  <Skeleton width={200} />
                </div>
                <div className='text-13 font-bold'>
                  <Skeleton width={50} />
                </div>
              </div>
            </div>
          </SwiperSlide>
        )
      })}
    </Swiper>
  )
}

const RecentlyViewed: React.FC<{ slidesPerView?: number; align?: 'center' | 'left' | 'right' }> = ({
  slidesPerView,
  align,
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const ref = useRef()
  const isVisible = useIsVisible(ref, { threshold: 0.25 })
  const { value: storageIds } = useContext(RecentlyViewedContext)
  const [activeProductData, setActiveProductData] = useState(null)
  const [loadReflections, reflectionsQueryResult] = useProductReflectionsLazyQuery({
    variables: {
      first: 20,
      idIn: storageIds.map(id => `${id}`),
      orderBy: [{ id: Ordering.Desc }],
      cartType: CartTypeEnum.Default,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (isVisible && storageIds.length && !reflectionsQueryResult.data) {
      loadReflections()
    }
  }, [isVisible, reflectionsQueryResult.data, storageIds.length])

  const reflections: HomeProductReflectionsQueryResult['data']['specifics']['edges'][0]['node'][] = useMemo(
    () =>
      maybe(
        () =>
          _.sampleSize(
            reflectionsQueryResult.data.specifics.edges.map(edge => edge.node),
            MAX_SHOW_PRODUCTS
          ),
        []
      ),
    [reflectionsQueryResult.data]
  )

  const swiperOptions: SwiperOptions = {
    spaceBetween: 10,
    slidesPerView: slidesPerView || 6,
    lazy: true,
    preloadImages: false,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-recently-viewed`,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: reflections.length > 1,
    allowSlidePrev: reflections.length > 1,
    autoplay: {
      disableOnInteraction: true,
      delay: 5000,
      waitForTransition: true,
    },

    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        speed: 300,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 300,
      },
      800: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 1000,
      },
      1000: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        speed: 1000,
      },
      1200: {
        slidesPerView: slidesPerView || 6,
        slidesPerGroup: slidesPerView || 6,
        speed: 1000,
      },
    },
  }
  const handleClickLink = spec => e => {
    e.preventDefault()
    setActiveProductData({ pk: spec.pk, slug: spec.slug })
  }
  return (
    <section className='my-20' ref={ref}>
      <div className='pt-20 px-12'>
        <h3
          className={`mb-40 font-medium text-4xl md:text-5xl uppercase leading-24 ${
            align ? `text-${align}` : 'text-left'
          }`}
        >
          {t('title.recentlyViewed', 'Recently Viewed')}
        </h3>
      </div>
      <div className='relative px-12 pb-20'>
        {!reflections.length && reflectionsQueryResult.loading && (
          <RecentlyViewedSkeleton swiperOptions={swiperOptions} />
        )}
        <Swiper {...swiperOptions}>
          {reflections.map((node, idx) => {
            const minPrice = node.product.priceRange.discount.min
            const maxPrice = node.product.priceRange.discount.max
            const currency = node.product.priceRange.currency
            return (
              <SwiperSlide key={idx}>
                <Link
                  className={`relative block no-underline ${classes.root}`}
                  to={getProductUrl(node.slug, node.pk)}
                  onClick={handleClickLink(node)}
                >
                  <div className='absolute inset-0 h-full'>
                    <LazyLoadImage
                      alt={node.name}
                      effect='opacity'
                      placeholderSrc={PlaceHolder}
                      src={node.product.thumbnail.lg}
                      className='w-full h-full object-contain object-top'
                    />
                  </div>
                  <div className={classes.placeHolder} />
                  <div className={`hidden md:block absolute bottom-0 left-0 right-0 p-12 ${classes.detailsWrapper}`}>
                    <div className='text-13 line-clamp-3'>{node.shortName || node.name}</div>
                    <div className='text-13 font-bold'>
                      <FormattedPrice currency={currency} amount={minPrice} />
                      {minPrice !== maxPrice && (
                        <>
                          {' - '}
                          <FormattedPrice currency={currency} amount={maxPrice} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className='md:hidden pt-12'>
                    <div className='text-13 line-clamp-3'>{node.shortName || node.name}</div>
                    <div className='text-13 font-bold'>
                      <FormattedPrice currency={currency} amount={minPrice} />
                      {minPrice !== maxPrice && (
                        <>
                          {' - '}
                          <FormattedPrice currency={currency} amount={maxPrice} />
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            )
          })}
        </Swiper>
        <div
          id={`swiper-pagination-recently-viewed`}
          className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
            reflections.length > 1 ? 'block' : 'hidden'
          } md:hidden`}
        />
      </div>
      <ProductDetailsDialog
        isOpen={!!activeProductData}
        setIsOpen={setActiveProductData}
        spec={reflections.find(spec => spec.pk === activeProductData?.pk)}
      />
    </section>
  )
}

export default RecentlyViewed
